/* iter104 — Monthly SMS cost recap (segments per event × unit cost). */
import { Fragment, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/\u202f|,/g, ' ');
const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const label = (m) => { const [y, mm] = m.split('-'); return `${MONTHS[Number(mm) - 1]} ${y}`; };

const SmsMonthlyStats = () => {
  const { user } = useAuth();
  const canEdit = ['admin', 'comptable'].includes(user?.role);
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [unit, setUnit] = useState('');
  const [open, setOpen] = useState(null);

  const load = () => api.get('/sms/stats/monthly', { params: { months } })
    .then(r => { setData(r.data); setUnit(String(r.data.unit_cost ?? '')); })
    .catch(e => toast.error(e?.response?.data?.detail || 'Erreur chargement du récap'));
  useEffect(() => { load(); }, [months]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveUnit = async () => {
    try { await api.put('/sms/stats/unit-cost', { unit_cost: Number(unit) || 0 }); toast.success('Coût unitaire enregistré'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (!data) return <div className="text-sm text-[#94A3B8]">Chargement…</div>;
  return (
    <div className="space-y-3" data-testid="sms-monthly-stats">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="flex items-center gap-1 text-xs text-[#64748B]">
          <span>Période</span>
          <select value={months} onChange={e => setMonths(Number(e.target.value))} data-testid="sms-stats-months"
            className="h-8 rounded-md border border-[#E2E8F0] px-2 text-sm bg-white">
            {[3, 6, 12].map(n => <option key={n} value={n}>{n} mois</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1 text-xs text-[#64748B]">
          <span>Coût / segment (FCFA)</span>
          <Input type="number" min="0" value={unit} onChange={e => setUnit(e.target.value)} disabled={!canEdit}
            className="h-8 w-24" data-testid="sms-unit-cost" />
          {canEdit && <Button size="sm" variant="outline" className="h-8" onClick={saveUnit} data-testid="sms-unit-cost-save">OK</Button>}
        </div>
        <div className="ml-auto flex gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-slate-100" data-testid="sms-stats-total-segments">Segments : <b>{fmt(data.total_segments)}</b></span>
          <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700" data-testid="sms-stats-total-cost">Coût estimé : <b>{fmt(data.total_cost)} FCFA</b></span>
        </div>
      </div>
      <div className="rounded-md border border-[#E2E8F0] overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Mois</TableHead><TableHead className="text-right">SMS</TableHead><TableHead className="text-right">Envoyés</TableHead>
            <TableHead className="text-right">Échecs</TableHead><TableHead className="text-right">Segments</TableHead><TableHead className="text-right">Coût (FCFA)</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {data.months.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#94A3B8] py-4">Aucun SMS sur la période</TableCell></TableRow>}
            {data.months.map(m => (
              <Fragment key={m.month}>
                <TableRow data-testid={`sms-month-${m.month}`}>
                  <TableCell className="font-medium capitalize">{label(m.month)}</TableCell>
                  <TableCell className="text-right">{m.total}</TableCell>
                  <TableCell className="text-right text-emerald-700">{m.sent}</TableCell>
                  <TableCell className="text-right text-red-600">{m.failed}</TableCell>
                  <TableCell className="text-right">{fmt(m.segments)}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(m.cost)}</TableCell>
                  <TableCell className="text-right">
                    <button type="button" className="text-xs text-[#0052FF]" onClick={() => setOpen(open === m.month ? null : m.month)} data-testid={`sms-month-toggle-${m.month}`}>
                      {open === m.month ? 'Masquer' : 'Détail par événement'}
                    </button>
                  </TableCell>
                </TableRow>
                {open === m.month && m.events.map(e => (
                  <TableRow key={m.month + e.event} className="bg-slate-50" data-testid={`sms-month-event-${m.month}-${e.event}`}>
                    <TableCell className="pl-8 text-xs text-[#64748B]">↳ {e.label}</TableCell>
                    <TableCell className="text-right text-xs">{e.count}</TableCell>
                    <TableCell colSpan={2} />
                    <TableCell className="text-right text-xs">{fmt(e.segments)}</TableCell>
                    <TableCell className="text-right text-xs">{fmt(e.cost)}</TableCell>
                    <TableCell />
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SmsMonthlyStats;
